// ── Agenda.js — Events page (timeline view + event/circle detail)
// This IS the Events page. It replaces the old Events.js in navigation.
// Upcoming/Mine/Past tabs | Events + Circles unified | Event modal inline

import { useState, useMemo, useEffect } from 'react';
import { useTr } from '../utils/useTr';
import { useApp } from '../contexts/AppContext';
import { getEventPrice, getSpotsLeft, formatEventDate } from '../utils/events';
import { LocationIcon, HiveIcon, StarIcon, VerifiedIcon } from '../components/icons';
import StripeCheckout, { RefundModal } from '../components/StripeCheckout';
import { isRefundEligible, getReceipts } from '../utils/stripe';
import {
  EventModal, TicketSheet, AttendeesSheet,
  downloadICS, googleCalendarUrl,
} from './Events';

// ── Design tokens ─────────────────────────────────────────────────────────
const G = {
  bg:      'var(--black)',
  card:    'var(--dark)',
  border:  'var(--border)',
  white:   'var(--white)',
  muted:   'var(--muted)',
  gold:    'var(--gold)',
  goldPale:'var(--gold-pale)',
  goldBdr: 'var(--gold-border)',
  teal:    '#1D9E75',
  tealPale:'rgba(29,158,117,0.1)',
  tealBdr: 'rgba(29,158,117,0.3)',
  purple:  '#A084C9',
  serif:   "'Cormorant Garamond',serif",
  sans:    "'Outfit',sans-serif",
};

// ── Inline SVGs ───────────────────────────────────────────────────────────
function CalendarIcon({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  );
}

function DinnerIcon({ size = 16, color = 'currentColor' }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11l19-9-9 19-2-8-8-2z"/></svg>;
}
function OutdoorIcon({ size = 16, color = 'currentColor' }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l4-8 4 4 3-6 4 10H3z"/></svg>;
}
function WorkshopIcon({ size = 16, color = 'currentColor' }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>;
}
function AperoIcon({ size = 16, color = 'currentColor' }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="8 22 12 17 16 22"/><path d="M7 11l5-9 5 9H7z"/></svg>;
}

// ── Circle type config ────────────────────────────────────────────────────
const SCOUT_TYPES = {
  dinner:   { label:'Dinner Circle', Icon: DinnerIcon,   color:'#C97A7A' },
  outdoor:  { label:'Outdoor',       Icon: OutdoorIcon,  color:'#7AA8C9' },
  workshop: { label:'Workshop',      Icon: WorkshopIcon, color:'#C9A84C' },
  apero:    { label:'Apéro Circle',  Icon: AperoIcon,    color:'#7EB8A0' },
};

function circleStatusColor(status) {
  if (status === 'confirmed') return G.teal;
  if (status === 'proposed')  return G.gold;
  if (status === 'waiting')   return G.purple;
  return G.muted;
}

// ── Agenda card ───────────────────────────────────────────────────────────
function AgendaCard({ item, currentUser, onOpen }) {
  const tr = useTr();
  const isScout = item._type === 'circle';

  const accentColor  = isScout ? G.teal : G.gold;
  const accentPale   = isScout ? G.tealPale : G.goldPale;
  const accentBorder = isScout ? G.tealBdr  : G.goldBdr;
  const scoutType   = SCOUT_TYPES[item.type];

  const dateStr = item.date || item.event_date;
  const timeStr = item.time || item.event_time;
  const dateObj = dateStr ? new Date(`${dateStr}T${timeStr || '00:00'}`) : null;
  const isToday    = dateObj && dateObj.toDateString() === new Date().toDateString();
  const isTomorrow = dateObj && dateObj.toDateString() === new Date(Date.now()+86400000).toDateString();
  const dayLabel  = isToday ? 'Today' : isTomorrow ? 'Tomorrow'
    : dateObj ? dateObj.toLocaleDateString([], { weekday:'short', month:'short', day:'numeric' }) : '';
  const timeLabel = dateObj && timeStr
    ? dateObj.toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' }) : '';

  const price = !isScout ? getEventPrice(item, currentUser) : null;

  return (
    <div onClick={() => onOpen(item)}
      style={{ background:G.card, border:`1px solid ${item._registered ? accentBorder : G.border}`, borderRadius:12, overflow:'hidden', cursor:'pointer', transition:'transform 0.15s, box-shadow 0.15s' }}
      onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.3)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)';    e.currentTarget.style.boxShadow='none'; }}
    >
      {/* Header strip */}
      {!isScout && item.image_url ? (
        <div style={{ height:120, overflow:'hidden', position:'relative' }}>
          <img src={item.image_url} alt={item.title} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
          <div style={{ position:'absolute', inset:0, background:'linear-gradient(transparent 40%, rgba(0,0,0,0.7))' }}/>
          <div style={{ position:'absolute', top:8, left:8 }}>
            <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:'0.6rem', padding:'2px 7px', borderRadius:99, background:'rgba(0,0,0,0.7)', color:G.gold, fontWeight:600, border:`1px solid ${G.goldBdr}` }}>
              <StarIcon size={10} color={G.gold}/> Event
            </span>
          </div>
          {price === 0 ? (
            <div style={{ position:'absolute', top:8, right:8, background:'rgba(29,158,117,0.85)', padding:'2px 8px', borderRadius:99 }}>
              <span style={{ fontSize:'0.62rem', fontWeight:700, color:'#fff' }}>Free</span>
            </div>
          ) : price > 0 ? (
            <div style={{ position:'absolute', top:8, right:8, background:'rgba(0,0,0,0.75)', padding:'2px 8px', borderRadius:99 }}>
              <span style={{ fontSize:'0.62rem', fontWeight:700, color:G.gold }}>${price}</span>
            </div>
          ) : null}
        </div>
      ) : isScout ? (
        <div style={{ height:52, background:`linear-gradient(135deg, ${G.tealPale}, rgba(0,0,0,0))`, display:'flex', alignItems:'center', padding:'0 14px', gap:10, borderBottom:`1px solid ${G.tealBdr}` }}>
          {scoutType?.Icon ? <scoutType.Icon size={16} color={scoutType.color || G.teal}/> : <HiveIcon size={16} color={G.teal}/>}
          <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:'0.62rem', padding:'2px 8px', borderRadius:99, background:G.tealPale, color:G.teal, fontWeight:600, border:`1px solid ${G.tealBdr}` }}>
            <HiveIcon size={10} color={G.teal}/>
            Scout · {scoutType?.label || item.type}
          </span>
        </div>
      ) : null}

      <div style={{ padding:'0.75rem' }}>
        {/* Date */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.35rem' }}>
          <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:'0.68rem', color: isToday ? accentColor : G.muted, fontWeight: isToday ? 700 : 400 }}>
            <CalendarIcon size={11} color={isToday ? accentColor : G.muted}/>
            {dayLabel}{timeLabel ? ` · ${timeLabel}` : ''}
          </span>
          {!isScout && getSpotsLeft(item) <= 5 && getSpotsLeft(item) > 0 && (
            <span style={{ fontSize:'0.6rem', color:'#E07070' }}>Only {getSpotsLeft(item)} spots</span>
          )}
          {isScout && (
            <span style={{ fontSize:'0.6rem', color: circleStatusColor(item.status), fontWeight:600, letterSpacing:'0.05em', textTransform:'uppercase' }}>
              {item.status}
            </span>
          )}
        </div>

        {/* Title */}
        <p style={{ fontSize:'0.92rem', fontWeight:600, color:G.white, margin:'0 0 0.2rem', lineHeight:1.3 }}>
          {!isScout ? item.title : (scoutType?.label || 'Scout')}
        </p>

        {/* Location */}
        <p style={{ display:'flex', alignItems:'center', gap:5, fontSize:'0.72rem', color:G.muted, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          <LocationIcon size={11} color={G.muted}/>
          {!isScout ? (item.location || item.address) : (item.venue_name || item.venue_area || 'Location TBD')}
        </p>

        {/* Circle interests */}
        {isScout && item.shared_interests?.length > 0 && (
          <div style={{ display:'flex', gap:'0.3rem', flexWrap:'wrap', marginTop:'0.5rem' }}>
            {item.shared_interests.slice(0,3).map(int => (
              <span key={int} style={{ fontSize:'0.6rem', padding:'1px 6px', background:G.tealPale, border:`1px solid ${G.tealBdr}`, borderRadius:99, color:G.teal }}>
                {int.replace('interest.','').replace(/_/g,' ')}
              </span>
            ))}
          </div>
        )}

        {/* Bottom row */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'0.6rem' }}>
          {isScout && item.members && (
            <span style={{ fontSize:'0.7rem', color:G.muted }}>
              {item.members.filter(m => m.confirmed).length}/{item.min_confirmations || 4} confirmed
            </span>
          )}
          {item._registered && (
            <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:'0.6rem', padding:'2px 8px', background:accentPale, border:`1px solid ${accentBorder}`, borderRadius:99, color:accentColor, fontWeight:600, marginLeft:'auto' }}>
              <VerifiedIcon size={10} color={accentColor}/>
              {isScout ? 'Joined' : 'Registered'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────
export default function Agenda() {
  const tr = useTr();
  const {
    currentUser, lang, events: dbEvents, circles,
    registeredEvents, setRegisteredEvents,
    attendedEvents,
    features, setPage,
    registerForEvent, cancelEventRegistration,
    joinEventWaitlist, leaveEventWaitlist, waitlistEvents,
    sponsors,
  } = useApp();

  const [tab,        setTab]        = useState('upcoming');
  const [typeFilter, setTypeFilter] = useState('all');

  // Event detail state
  const [openEvent,      setOpenEvent]      = useState(null);
  const [showQR,         setShowQR]         = useState(false);
  const [showCheckout,   setShowCheckout]   = useState(false);
  const [showRefund,     setShowRefund]     = useState(false);
  const [showAttendees,  setShowAttendees]  = useState(false);
  const [receipts, setReceipts] = useState([]);
  useEffect(() => {
    if (currentUser?.id) getReceipts(currentUser.id).then(setReceipts).catch(() => {});
  }, [currentUser?.id]);

  const regSet = registeredEvents || new Set();
  const attSet = attendedEvents   || new Set();

  // Normalize events
  const eventItems = useMemo(() =>
    (dbEvents || []).map(ev => ({
      ...ev, _type:'event',
      _registered: regSet.has(ev.id),
      _attended:   attSet.has(ev.id),
      _sortDate:   ev.date ? new Date(`${ev.date}T${ev.time||'00:00'}`) : new Date(0),
    })), [dbEvents, regSet, attSet]);

  // Normalize circles
  const circleItems = useMemo(() =>
    (circles || []).map(ci => ({
      ...ci, _type:'circle',
      _registered: ci.members?.some(m => m.id === currentUser?.id && m.confirmed),
      _sortDate:   ci.event_date ? new Date(`${ci.event_date}T${ci.event_time||'00:00'}`) : new Date(0),
    })), [circles, currentUser]);

  const now = new Date();

  const allItems = useMemo(() => {
    let items = [...eventItems, ...circleItems];
    if (typeFilter !== 'all') items = items.filter(i => i._type === typeFilter);

    switch (tab) {
      case 'upcoming':
        return items
          .filter(i => {
            if (i._type === 'event')  return i.status === 'published' && i._sortDate >= now;
            if (i._type === 'circle') return ['waiting','proposed','confirmed'].includes(i.status) && i._sortDate >= now;
            return false;
          })
          .sort((a, b) => a._sortDate - b._sortDate);
      case 'mine':
        return items.filter(i => i._registered).sort((a, b) => a._sortDate - b._sortDate);
      case 'past':
        return items
          .filter(i => {
            if (i._type === 'event')  return i.status === 'past' || (i._sortDate < now && i._registered);
            if (i._type === 'circle') return i.status === 'past';
            return false;
          })
          .sort((a, b) => b._sortDate - a._sortDate);
      default: return items;
    }
  }, [eventItems, circleItems, tab, typeFilter, now]);

  const myCount = [...eventItems, ...circleItems].filter(i => i._registered).length;
  const upcomingEvents = eventItems.filter(i => i.status === 'published');

  // Register handler
  const handleRegister = async (event) => {
    if (!currentUser) { setPage('auth'); return; }
    const spotsLeft = getSpotsLeft(event);
    if (spotsLeft <= 0 && !regSet.has(event.id)) {
      if (waitlistEvents?.has(event.id)) await leaveEventWaitlist?.(event.id);
      else await joinEventWaitlist?.(event.id);
      return;
    }
    const price = getEventPrice(event, currentUser);
    if (price === 0) {
      const { error } = await registerForEvent(event.id, null, 0);
      if (!error) setRegisteredEvents(prev => new Set([...prev, event.id]));
    } else {
      setOpenEvent(event);
      setShowCheckout(true);
    }
  };

  const openItem = (item) => {
    if (item._type === 'event')  setOpenEvent(item);
    if (item._type === 'circle') setPage('circles');
  };

  const closeEvent = () => {
    setOpenEvent(null);
    setShowQR(false);
    setShowAttendees(false);
    setShowCheckout(false);
    setShowRefund(false);
  };

  const eventsEnabled  = features?.events  !== false;
  const circlesEnabled = features?.circles !== false;

  if (!eventsEnabled && !circlesEnabled) {
    return (
      <div style={{ minHeight:'100vh', background:G.bg, display:'flex', alignItems:'center', justifyContent:'center', padding:'2rem', textAlign:'center' }}>
        <div>
          <div style={{ marginBottom:'1rem', display:'flex', justifyContent:'center' }}>
            <CalendarIcon size={48} color="var(--gold)"/>
          </div>
          <h2 style={{ fontFamily:G.serif, fontSize:'1.6rem', fontWeight:400, color:G.white, marginBottom:'0.5rem' }}>
            {tr('events.coming_soon') || 'Events coming soon'}
          </h2>
          <p style={{ fontSize:'0.82rem', color:G.muted, lineHeight:1.7 }}>
            {tr('events.coming_sub2')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight:'100vh', background:G.bg, fontFamily:G.sans }}>
      {/* Header */}
      <div style={{ padding:'1rem 1.2rem 0', position:'sticky', top:0, background:G.bg, zIndex:10, borderBottom:`1px solid ${G.border}` }}>
        <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', marginBottom:'0.8rem' }}>
          <h1 style={{ fontFamily:G.serif, fontSize:'1.6rem', fontWeight:400, color:G.white, margin:0 }}>
            {tr('events.title') || 'Events'} <span style={{ color:G.gold, fontStyle:'italic', fontSize:'1.2rem' }}>{tr('events.city_label') || ''}</span>
          </h1>
          {/* Type filter */}
          {eventsEnabled && circlesEnabled && (
            <div style={{ display:'flex', gap:4 }}>
              {[
                { id:'all', label:'All', Icon:null },
                { id:'event', label:'Events', Icon:StarIcon },
                { id:'circle', label:'Scout', Icon:HiveIcon },
              ].map(({ id, label, Icon }) => (
                <button key={id} onClick={() => setTypeFilter(id)} style={{
                  display:'inline-flex', alignItems:'center', gap:4,
                  padding:'3px 10px', borderRadius:99,
                  border:`1px solid ${typeFilter===id ? G.gold : G.border}`,
                  background: typeFilter===id ? G.goldPale : 'transparent',
                  color: typeFilter===id ? G.gold : G.muted,
                  fontSize:'0.7rem', cursor:'pointer',
                }}>
                  {Icon && <Icon size={10} color={typeFilter===id ? G.gold : G.muted}/>}
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Calendar export */}
        {upcomingEvents.length > 0 && (tab === 'upcoming' || tab === 'all') && (
          <div style={{ display:'flex', gap:'0.5rem', alignItems:'center', marginBottom:'0.6rem', flexWrap:'wrap' }}>
            <span style={{ fontSize:'0.6rem', color:G.muted }}>{tr('events.export') || 'Export:'}</span>
            <button onClick={() => downloadICS(upcomingEvents)} style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', background:'transparent', border:`1px solid ${G.border}`, borderRadius:99, color:G.muted, fontSize:'0.6rem', cursor:'pointer' }}>
              <CalendarIcon size={10} color={G.muted}/> .ics / Apple
            </button>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display:'flex', gap:0 }}>
          {[
            { id:'upcoming', label: tr('events.tab_upcoming') || 'Upcoming' },
            { id:'mine',     label: `${tr('agenda.tab_mine') || 'My schedule'}${myCount ? ` (${myCount})` : ''}` },
            { id:'past',     label: tr('events.tab_past') || 'Past' },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              flex:1, padding:'0.6rem 0.5rem', border:'none',
              borderBottom:`2px solid ${tab===t.id ? G.gold : 'transparent'}`,
              background:'transparent', color: tab===t.id ? G.gold : G.muted,
              fontSize:'0.78rem', fontWeight: tab===t.id ? 600 : 400,
              cursor:'pointer', transition:'all 0.15s',
            }}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding:'1rem 1rem 5rem' }}>
        {allItems.length === 0 ? (
          <div style={{ textAlign:'center', padding:'3rem 1rem' }}>
            <div style={{ marginBottom:'0.8rem', display:'flex', justifyContent:'center' }}>
              <CalendarIcon size={40} color="rgba(255,255,255,0.15)"/>
            </div>
            <p style={{ fontFamily:G.serif, fontSize:'1.2rem', color:G.white, marginBottom:'0.3rem' }}>
              {tab === 'mine'
                ? (tr('agenda.none_mine') || 'Nothing in your schedule yet')
                : tab === 'past'
                ? (tr('events.no_past') || 'No past events')
                : (tr('events.no_upcoming') || 'No upcoming events')}
            </p>
            <p style={{ fontSize:'0.78rem', color:G.muted, lineHeight:1.6 }}>
              {tab === 'mine'
                ? (tr('agenda.none_mine_sub') || 'Register for an event or join a Circle.')
                : tab === 'upcoming'
                ? 'BeeLove events for your network will appear here.'
                : ''}
            </p>
          </div>
        ) : tab === 'upcoming' ? (() => {
          // Group by date
          const groups = {};
          allItems.forEach(item => {
            const label = item._sortDate > new Date(0)
              ? item._sortDate.toLocaleDateString([], { weekday:'long', month:'long', day:'numeric' })
              : 'Date TBD';
            if (!groups[label]) groups[label] = [];
            groups[label].push(item);
          });
          return Object.entries(groups).map(([dateLabel, items]) => (
            <div key={dateLabel} style={{ marginBottom:'1.5rem' }}>
              <p style={{ display:'flex', alignItems:'center', gap:6, fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.12em', color:G.muted, textTransform:'uppercase', marginBottom:'0.6rem' }}>
                <CalendarIcon size={10} color={G.muted}/>{dateLabel}
              </p>
              <div style={{ display:'flex', flexDirection:'column', gap:'0.7rem' }}>
                {items.map(item => (
                  <AgendaCard key={`${item._type}-${item.id}`} item={item} currentUser={currentUser} onOpen={openItem}/>
                ))}
              </div>
            </div>
          ));
        })() : (
          <div style={{ display:'flex', flexDirection:'column', gap:'0.7rem' }}>
            {allItems.map(item => (
              <AgendaCard key={`${item._type}-${item.id}`} item={item} currentUser={currentUser} onOpen={openItem}/>
            ))}
          </div>
        )}
      </div>

      {/* ── Event detail modal ── */}
      {openEvent && !showQR && !showCheckout && !showRefund && !showAttendees && (
        <EventModal
          event={openEvent}
          currentUser={currentUser}
          lang={lang}
          onClose={closeEvent}
          registered={regSet.has(openEvent.id)}
          attended={attSet.has(openEvent.id)}
          onRegister={handleRegister}
          onShowQR={() => setShowQR(true)}
          onShowAttendees={() => setShowAttendees(true)}
          onShowRefund={() => setShowRefund(true)}
        />
      )}

      {/* QR Ticket */}
      {showQR && openEvent && (
        <TicketSheet
          event={openEvent}
          currentUser={currentUser}
          attended={attSet.has(openEvent.id)}
          onClose={() => setShowQR(false)}
          tr={tr}
        />
      )}

      {/* Attendees */}
      {showAttendees && openEvent && (
        <AttendeesSheet event={openEvent} onClose={() => setShowAttendees(false)} tr={tr}/>
      )}

      {/* Stripe checkout */}
      {showCheckout && openEvent && (
        <div onClick={() => setShowCheckout(false)} style={{ position:'fixed', inset:0, zIndex:900, background:'var(--overlay-modal)', backdropFilter:'blur(8px)', display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background:'var(--black)', border:'1px solid var(--border)', maxWidth:480, width:'100%', borderRadius:'var(--r-xl) var(--r-xl) 0 0', marginBottom:'var(--bottom-h, 56px)', maxHeight:'calc(92vh - var(--bottom-h,56px))', overflowY:'auto' }}>
            <StripeCheckout
              event={openEvent}
              onClose={() => setShowCheckout(false)}
              onSuccess={async (receipt) => {
                await registerForEvent(openEvent.id, receipt.paymentId, receipt.amount);
                setRegisteredEvents(prev => new Set([...prev, openEvent.id]));
                setShowCheckout(false);
                setShowQR(true);
              }}
            />
          </div>
        </div>
      )}

      {/* Refund modal */}
      {showRefund && openEvent && (() => {
        const receipt = receipts.find(r => r.eventId === openEvent.id);
        return (
          <div onClick={() => setShowRefund(false)} style={{ position:'fixed', inset:0, zIndex:900, background:'var(--overlay-modal)', backdropFilter:'blur(8px)', display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
            <div onClick={e => e.stopPropagation()} style={{ background:'var(--black)', border:'1px solid var(--border)', maxWidth:480, width:'100%', borderRadius:'var(--r-xl) var(--r-xl) 0 0', marginBottom:'var(--bottom-h, 56px)' }}>
              <RefundModal
                event={openEvent}
                receipt={receipt}
                onClose={() => setShowRefund(false)}
                onRefunded={() => {
                  setRegisteredEvents(r => { const n = new Set(r); n.delete(openEvent.id); return n; });
                  setShowRefund(false);
                  setOpenEvent(null);
                }}
              />
            </div>
          </div>
        );
      })()}
    </div>
  );
}
